import { Grid, Paper, Typography } from 'mui/core';
import { styled } from '@mui/material/styles';
import cx from 'classnames';
import { SkeletonBar } from '../Skeleton';
import getScrollBarWidth from '@shared/lib/getScrollBarWidth';

export const FAKE_TITLE_DATA = ' ';

const PREFIX = 'ContentWrapper';

const classes = {
  paper: `${PREFIX}-paper`,
  titleBox: `${PREFIX}-titleBox`,
  titleItem: `${PREFIX}-titleItem`,
  titleTextBox: `${PREFIX}-titleTextBox`,
  underTitleBox: `${PREFIX}-underTitleBox`,
  actions: `${PREFIX}-actions`,
  container: `${PREFIX}-container`,
};

const Root = styled('div', {
  name: PREFIX,
})(({ theme }) => ({
  display: 'contents',
  [`& .${classes.paper}`]: {
    padding: theme.spacing(1.25, 3, 0, 3),
    width: '100%',
    height: '100%',
    borderRadius: 0,
    overflow: 'hidden',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    // Was a literal 'white', which painted a full-page white sheet in
    // dark mode — most visible as a white flash behind the loader on
    // Edit Order. The token is #ffffff in light mode, so light is
    // unchanged.
    backgroundColor: 'var(--qhs-surface)',
  },
  [`& .${classes.titleBox}`]: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.lightBlue,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  [`& .${classes.titleItem}`]: {
    position: 'relative',
    marginLeft: 4,
    fontWeight: 500,
    fontSize: 36,
  },
  [`& .${classes.titleTextBox}`]: {
    maxHeight: 35,
    width: '100%',
    minWidth: 250,
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.underTitleBox}`]: {
    fontSize: 15,
    letterSpacing: 0,
    display: 'flex',
    color: theme.colors.blue2,
    marginLeft: 4,
  },
  [`& .${classes.actions}`]: {
    height: 49,
    display: 'flex',
  },
  [`& .${classes.container}`]: {
    paddingBottom: theme.spacing(3),
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    '--webkit-overflow-scrolling': 'touch',
    '--webkit-transform': 'translate3d(0, 0, 0)',
  },
}));

const ContentWrapper = ({
  title,
  titleText,
  additionTitleButton,
  actions,
  children,
  className,
  additionalNode,
  underTitleText,
  scrollBarSupport,
  titleClassName,
  underTitleTextStyle,
  actionsStyle,
  containerStyle = {},
  titleContainerStyle = {},
  titleStyle = {},
  maxWidth,
  ...rest
}) => {
  const scrollBarWidth = scrollBarSupport ? getScrollBarWidth() + 1.5 : 0;

  return (
    <Root>
      <Paper className={cx(classes.paper, className)} {...rest}>
        <Grid
          container
          justifyContent='space-between'
          alignItems='center'
          direction='row'
          style={titleContainerStyle}
        >
          <Grid item style={{ flexWrap: 'nowrap', ...titleStyle }}>
            <Grid container justifyContent='flex-start' alignItems='center' style={{ flexWrap: 'nowrap' }}>
              {title && <div className={cx(classes.titleBox, titleClassName)}>{title}</div>}
              {titleText === FAKE_TITLE_DATA && (
                <Grid
                  item
                  className={classes.titleTextBox}
                  style={{
                    maxWidth: maxWidth || 700,
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <SkeletonBar variant='text' width='100%' height={56} />
                  </div>
                </Grid>
              )}
              {titleText && titleText !== FAKE_TITLE_DATA && (
                <Grid
                  item
                  className={classes.titleTextBox}
                  style={{
                    maxWidth: maxWidth || 700,
                  }}
                >
                  <Typography noWrap className={classes.titleItem}>
                    {titleText}
                  </Typography>
                </Grid>
              )}
              {additionTitleButton}
            </Grid>
            {underTitleText && (
              <Typography className={cx(classes.underTitleBox, underTitleTextStyle)}>
                {underTitleText}
              </Typography>
            )}
          </Grid>
          {!!actions && (
            <Grid
              item
              className={cx(classes.actions, actionsStyle)}
              style={{
                paddingRight: scrollBarWidth > 0 ? scrollBarWidth * 8 : undefined,
              }}
            >
              {actions}
            </Grid>
          )}
        </Grid>
        {!!additionalNode && additionalNode}
        <div className={cx(classes.container)} style={containerStyle} id={'contentArea'}>
          {children}
        </div>
      </Paper>
    </Root>
  );
};

export default ContentWrapper;
